import React from 'react';
import { Link, Route, Routes, useNavigate } from 'react-router-dom';
import { Alert, Button, ConfigProvider, Space } from 'antd';
import {
	IdcardOutlined,
	UserOutlined,
	LogoutOutlined,
	LineChartOutlined,
	FolderOutlined,
	CreditCardOutlined,
	BookOutlined,
	SolutionOutlined,
	SettingOutlined,
	InfoCircleOutlined,
	DollarCircleOutlined,
	CarryOutOutlined,
	DeleteColumnOutlined,
} from '@ant-design/icons';
import {
	Drivers,
	DriverDetails,
	DriverCard,
	NewDriver,
	Containers,
	ContainerDetails,
	NewContainer,
	NotFound,
	Clients,
	ClientDetails,
	NewClient,
	Expenses,
	NewExpense,
	GetMoney,
	Orders,
	OrderDetails,
	NewOrder,
	Tasks,
	TaskDetails,
	ContainerSelection,
	TaskClosingScreen,
	Sign,
	SavedSign,
	Settings,
	Stats,
	FastActions,
	Finances,
	NewTaskDetails,
	NewTaskClosing,
	DeliveredLooseOrders,
} from '../pages';
import { IconCircle } from '../components.js';
import { Formatting } from '../utils';
import { usePanel } from '../reducers/index';
import locale from 'antd/lib/locale/pl_PL';
import moment from 'moment';
import 'moment/locale/pl';

const Main: React.FC = () => {
	const navigate = useNavigate();

	const {
		stats,
		deliveredLooseOrders,
		balance,
		level,
		admin,
		showToClaim,
		showUnclosed,
		setShowToClaim,
		setShowUnclosed,
	} = usePanel();

	const driverIcons = [
		{
			label: 'Kontenery',
			icon: <FolderOutlined />,
			route: '/panel/containers',
			notifications: null,
		},
		{
			label: 'Wydatki',
			icon: <CreditCardOutlined />,
			route: '/panel/expenses',
			notifications: null,
		},
		{
			label: 'Zamówienia',
			icon: <SolutionOutlined />,
			route: '/panel/orders',
			notifications: null,
		},
	];

	const adminIcons = [
		{
			label: 'Pracownicy',
			icon: <IdcardOutlined />,
			route: '/panel/drivers',
		},
		{
			label: 'Klienci',
			icon: <UserOutlined />,
			route: '/panel/clients',
		},
		{
			label: 'Kontenery',
			icon: <FolderOutlined />,
			route: '/panel/containers',
		},
		{
			label: 'Wydatki',
			icon: <CreditCardOutlined />,
			route: '/panel/expenses',
		},
		{
			label: 'Terminarz',
			icon: <BookOutlined />,
			route: '/panel/tasks',
			notifications: stats?.tasks,
		},
		{
			label: 'Zamówienia',
			icon: <SolutionOutlined />,
			route: '/panel/orders',
		},
	];

	const bottomIcons = admin
		? [
				{
					label: 'Ustawienia',
					icon: <SettingOutlined />,
					route: '/panel/settings',
				},
				{
					label: 'Statystyki',
					icon: <LineChartOutlined />,
					route: '/panel/stats',
					level: 2,
				},
				{
					label: 'Wyloguj',
					icon: <LogoutOutlined />,
					route: '/logout',
				},
		  ]
		: [
				{
					label: 'Ustawienia',
					icon: <SettingOutlined />,
					route: '/panel/settings',
				},
				{
					label: 'Wyloguj',
					icon: <LogoutOutlined />,
					route: '/logout',
				},
		  ];

	const toClaim = stats?.to_claim?.map((el) => (
		<tr className="claim-entry" key={`${el.id}`}>
			<td>{el.container.name}</td>
			<td>{el.size?.name}</td>
			<td>
				{el.order.address} - <Link to={`/panel/clients/${el.client?.id}`}>{el.client?.name}</Link>
			</td>
			<td>{<InfoCircleOutlined onClick={() => navigate(`/panel/tasks/${el.order.id}/2`)} />}</td>
		</tr>
	));

	const unclosed = stats?.unclosed?.map((el) => (
		<tr className="claim-entry" key={`${el.id}`}>
			<td>
				{el.address} - <Link to={`/panel/clients/${el.client_id}`}>{el.client.name}</Link>
			</td>
			<td>
				{Formatting.epochToDate(
					el.delivery_done ? el.claiming_date : el.delivery_date,
					moment(el.delivery_done ? el.claiming_date : el.delivery_date).hours() === 0
				)}
			</td>
			<td>{<InfoCircleOutlined onClick={() => navigate(`/panel/orders/${el.id}`)} />}</td>
		</tr>
	));

	return (
		<div className="panel flex flex-dir flex-jsb">
			{stats?.to_claim?.length > 0 && (
				<div className="claim">
					<Alert
						className="alert"
						message={[
							'Kontenery czekające na odbiór:',
							<span key="0" className="claim">
								{stats?.to_claim?.length}
							</span>,
						]}
						banner
						action={
							<Space>
								<Button size="large" onClick={() => setShowToClaim((curr) => !curr)}>
									{showToClaim ? 'Ukryj' : 'Pokaż'} kontenery
								</Button>
							</Space>
						}
						closable
					/>
					{showToClaim && (
						<div className="toClaim">
							<table>
								<tbody>
									<tr>
										<th>Numer</th>
										<th>Rozmiar</th>
										<th>Adres</th>
									</tr>
									{toClaim}
								</tbody>
							</table>
						</div>
					)}
				</div>
			)}

			{stats?.unclosed?.length > 0 && (
				<div className="claim">
					<Alert
						className="alert"
						message={[
							'Niezamknięte zamówienia:',
							<span key="0" className="claim">
								{stats?.unclosed?.length}
							</span>,
						]}
						banner
						action={
							<Space>
								<Button size="large" onClick={() => setShowUnclosed((curr) => !curr)}>
									{showToClaim ? 'Ukryj' : 'Pokaż'} zamówienia
								</Button>
							</Space>
						}
						closable
					/>
					{showUnclosed && (
						<div className="toClaim">
							<table>
								<tbody>
									<tr>
										<th>Adres - klient</th>
										<th>Data</th>
									</tr>
									{unclosed}
								</tbody>
							</table>
						</div>
					)}
				</div>
			)}

			{admin ? (
				<>
					{adminIcons.map((el) => (
						<div key={el.label} className="flex flex-jc flex-ac">
							<IconCircle
								key={el.label}
								label={el.label}
								onClick={() => navigate(el.route)}
								notifications={el.notifications}
							>
								{el.icon}
							</IconCircle>
						</div>
					))}
					<div key="99" className="fast-actions flex flex-jc flex-ac">
						<IconCircle key="99" label="Kontenery w zamówieniach" onClick={() => navigate('/panel/fast-actions')}>
							<FolderOutlined />
						</IconCircle>
					</div>
					<div>
						<IconCircle key="999" label="Podstawione bagi" onClick={() => navigate('/panel/orders/loose/delivered')}>
							<DeleteColumnOutlined style={{ transform: 'rotate(180deg)' }} />
						</IconCircle>
					</div>
					<div key="100" className="flex flex-jc flex-ac">
						{level > 1 && (
							<IconCircle key="100" label="Finanse" onClick={() => navigate('/panel/finances')}>
								<DollarCircleOutlined />
							</IconCircle>
						)}
					</div>
				</>
			) : (
				<>
					{driverIcons.map((el) => (
						<div key={el.label} className="flex flex-jc flex-ac">
							<IconCircle
								key={el.label}
								label={el.label}
								onClick={() => navigate(el.route)}
								notifications={el.notifications}
							>
								{el.icon}
							</IconCircle>
						</div>
					))}
					<div></div>
					<div className="flex flex-jc flex-ac">
						<IconCircle label="Terminarz" onClick={() => navigate('/panel/tasks')} notifications={stats?.tasks}>
							<BookOutlined />
						</IconCircle>
					</div>
					<div></div>
				</>
			)}

			{stats && deliveredLooseOrders && (
				<div className="middle-section flex flex-dir flex-ac">
					<table>
						<tbody>
							{!admin && (
								<tr key={'balance'}>
									<td>Stan konta:</td>
									<td style={{ whiteSpace: 'nowrap' }}>{Formatting.money(balance)}</td>
								</tr>
							)}
							{Object.entries(stats.summary.free).map(([k, v]) => (
								<tr key={k}>
									<td>Kontenery wolne {(v as any).name}:</td>
									<td>{(v as any).count}</td>
								</tr>
							))}
							<tr>
								<td>Kontenery do podstawienia:</td>
								<td>{stats.summary.to_deliver ?? 0}</td>
							</tr>
							<tr>
								<td>Kontenery podstawione:</td>
								<td>{stats.summary.delivered ?? 0}</td>
							</tr>
							<tr>
								<td>Kontenerów do wymiany:</td>
								<td>{stats.summary.to_replace ?? 0}</td>
							</tr>
							<tr>
								<td>Kontenerów czekających na odbiór:</td>
								<td>{stats.summary.to_claim ?? 0}</td>
							</tr>
							<tr>
								<td>Kontenery w transporcie:</td>
								<td>{stats.summary.in_transit ?? 0}</td>
							</tr>
							<tr>
								<td>Kontenery wypożyczone:</td>
								<td>{stats.summary.lended ?? 0}</td>
							</tr>
							<tr>
								<td>Bagi podstawione:</td>
								<td>{deliveredLooseOrders.reduce((acc, o) => acc + o.count, 0)}</td>
							</tr>
						</tbody>
					</table>
				</div>
			)}

			{admin ? (
				bottomIcons.map((el) => (
					<div key={el.label} className="flex flex-jc flex-ac">
						{level >= (el.level ?? 0) && (
							<IconCircle key={el.label} label={el.label} onClick={() => navigate(el.route)}>
								{el.icon}
							</IconCircle>
						)}
					</div>
				))
			) : (
				<>
					<div className="flex flex-jc flex-ac">
						<IconCircle label="Ustawienia" onClick={() => navigate('panel/settings')}>
							<SettingOutlined />
						</IconCircle>
					</div>
					<div className="flex flex-jc flex-ac">
						<IconCircle
							label="Zamknij kartę"
							onClick={() => navigate(`/panel/drivers/${sessionStorage.getItem('id')}/card`)}
						>
							<CarryOutOutlined />
						</IconCircle>
					</div>
					<div className="flex flex-jc flex-ac">
						<IconCircle label="Wyloguj" onClick={() => navigate('/logout')}>
							<LogoutOutlined />
						</IconCircle>
					</div>
				</>
			)}
		</div>
	);
};

export const Panel: React.FC = () => {
	return (
		<ConfigProvider locale={locale}>
			<Routes>
				<Route path="" element={<Main />} />
				<Route path="/drivers" element={<Drivers />} />
				<Route path="/drivers/:id" element={<DriverDetails />} />
				<Route path="/drivers/:id/card" element={<DriverCard />} />
				<Route path="/new-driver" element={<NewDriver />} />
				<Route path="/containers" element={<Containers />} />
				<Route path="/containers/:id" element={<ContainerDetails />} />
				<Route path="/new-container" element={<NewContainer />} />
				<Route path="/clients" element={<Clients />} />
				<Route path="/clients/:id" element={<ClientDetails />} />
				<Route path="/new-client" element={<NewClient />} />
				<Route path="/expenses" element={<Expenses />} />
				<Route path="/expenses/get-money" element={<GetMoney />} />
				<Route path="/expenses/:id" element={<Expenses />} />
				<Route path="/new-expense" element={<NewExpense />} />
				<Route path="/orders" element={<Orders />} />
				<Route path="/orders/loose/delivered" element={<DeliveredLooseOrders />} />
				<Route path="/orders/:id" element={<OrderDetails />} />
				<Route path="/orders/:id/:type" element={<OrderDetails />} />
				<Route path="/new-order" element={<NewOrder />} />
				<Route path="/tasks" element={<Tasks />} />
				<Route path="/tasks/:id/sign" element={<SavedSign />} />
				<Route path="/tasks/:id/:type" element={<TaskDetails />} />
				<Route path="/v2/tasks/:id" element={<NewTaskDetails />} />
				<Route path="/v2/tasks/:id/close" element={<NewTaskClosing />} />
				<Route path="/tasks/:id/:type/:sizeId" element={<ContainerSelection />} />
				<Route path="/task-closing/sign/:id" element={<Sign />} />
				<Route path="/task-closing/:id/:type/*" element={<TaskClosingScreen />} />
				<Route path="/settings" element={<Settings />} />
				<Route path="/stats" element={<Stats />} />
				<Route path="/fast-actions" element={<FastActions />} />
				<Route path="/finances" element={<Finances />} />
				<Route path="*" element={<NotFound />} />
			</Routes>
		</ConfigProvider>
	);
};
